import { AccountSettingsPageTab } from './types/AccountSettings.types';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBaseUrl } from '@/redux/modules/config';
import { getIsReseller } from '@/redux/modules/reseller';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import { ModifyQueryParamsAction } from '@liveauctioneers/caterwaul-components/lib/hooks/queryParams/useModifyQueryParams';
import { useAppSelector } from '@/redux/hooks';
import { useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';
import { Warning } from '@liveauctioneers/hammer-ui-core/icons';
import AccountSettingsPageHeaderMeta from './AccountSettingsPageHeaderMeta';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import PageTitle from '@/components/PageTitle/PageTitle';
import PreferencesForm from './PreferencesForm';
import ProfileForm from './ProfileForm';
import styled from 'styled-components';
import Tab from '@liveauctioneers/caterwaul-components/lib/Tab/Tab';
import Tabs from '@liveauctioneers/caterwaul-components/lib/Tab/Tabs';
import TaxCertManagementForm from './TaxCertManagementForm';
import UnsubscribeEmail from './UnsubscribeEmail';
import useAccountSettingsPageTab from './hooks/useAccountSettingsPageTab';

const AccountSettingsPage = () => {
    const isReseller = useAppSelector(getIsReseller);
    const { formatMessage } = useIntl();
    const tab = useAccountSettingsPageTab();
    const baseUrl = useAppSelector(getBaseUrl);
    const { error: editProfileError, errorMessage } = useAppSelector(getUiUpdateAccount);

    const profileTitle = (
        <>
            <StyledBigSpan>{formatMessage({ id: 'my_profile' })}</StyledBigSpan>
            <StyledSmallSpan>{formatMessage({ id: 'profile' })}</StyledSmallSpan>
        </>
    );

    const taxCertManagementTitle = <span>{formatMessage({ id: 'certManagement.taxes' })}</span>;

    const preferencesTitle = <span>{formatMessage({ id: 'preferences' })}</span>;

    const modifyQueryParams = useModifyQueryParams();

    const onTabChange = ({ currentTarget }: any) => {
        const { name } = currentTarget;
        if (name === AccountSettingsPageTab.Profile) {
            modifyQueryParams({
                action: ModifyQueryParamsAction.REMOVE,
                params: 'tab',
            });
        } else if (tab !== name) {
            modifyQueryParams({
                action: ModifyQueryParamsAction.ADD,
                query: { tab: name },
            });
        }
    };

    return (
        <StyledPageContainer>
            <UnsubscribeEmail />
            <AccountSettingsPageHeaderMeta baseUrl={baseUrl} />
            <CompleteAccountBanner />
            {editProfileError && (
                <StyledAccountUpdateErrorText>
                    <Warning
                        className="mr-lg"
                        color="critical"
                        size="sm"
                        style="fill"
                    />
                    <p>
                        {errorMessage ? (
                            <FormattedMessage
                                id="account_update_custom_error"
                                values={{ error: errorMessage }}
                            />
                        ) : (
                            <FormattedMessage id="account_update_error" />
                        )}
                    </p>
                </StyledAccountUpdateErrorText>
            )}
            <StyledPageTitle title={<FormattedMessage id="accountSettings.title" />} />
            <StyledTabs
                onChange={onTabChange}
                startingTabName={tab}
            >
                <Tab
                    label={profileTitle}
                    name={AccountSettingsPageTab.Profile}
                >
                    <ProfileForm />
                </Tab>
                {isReseller && (
                    <Tab
                        label={taxCertManagementTitle}
                        name={AccountSettingsPageTab.TaxCertManagement}
                        testid="taxCertificationTabLink"
                    >
                        <TaxCertManagementForm />
                    </Tab>
                )}
                <Tab
                    label={preferencesTitle}
                    name={AccountSettingsPageTab.Preferences}
                    testid="preferancesTab"
                >
                    <PreferencesForm />
                </Tab>
            </StyledTabs>
        </StyledPageContainer>
    );
};

export default AccountSettingsPage;

const StyledBigSpan = styled.span`
    display: inline;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const StyledSmallSpan = styled.span`
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: inline;
    }
`;

const StyledPageContainer = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto 64px;
    width: 100%;
    padding: 0 20px;
`;

const StyledAccountUpdateErrorText = styled.div`
    display: flex;
    flex-direction: row;
    margin: 20px 0;
    padding: 10px;
    background-color: ${({ theme }) => theme.colors.beige100};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    border-radius: 5px;
    align-items: center;
    align-self: center;
    width: 100%;
    p {
        margin: 0;
    }
`;

const StyledPageTitle = styled(PageTitle)`
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    align-self: center;
    width: 100%;
    margin: 32px 0 24px;
    display: flex;
    flex-direction: row;
`;

const StyledTabs = styled(Tabs)`
    align-self: center;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin-bottom: 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 24px;
    }
`;
